import { zoneLabel, type AutoBetSkip, type ZoneBounds } from './birdieAutoBet'
import type { Translate } from './tileViewModel'

/**
 * The auto-bet sheet's, pill's and notices' words (B32).
 *
 * A25 — THE EXTRACTOR IS LITERAL-ONLY. Every sentence is written out in full
 * here, once, as the first argument of a `t()` call; a parameterised one still
 * names its key literally and hands the values over as the options object, so
 * nothing in this feature ever feeds a computed key through `t()`.
 *
 * THREE are borrowed: `Exact order` is the board's own column heading, `Close`
 * the round sheet's cross, and `Auto bet off` the platform's own word for the
 * shared toggle — already in all sixteen locales, and in the sweepstakes one
 * as "Auto play off". Everything else is new to the widget and filled in all
 * sixteen locales by `scripts/birdieLocales.mjs`, gated by
 * `birdieLocales.test.ts` and, for `en` and `en-soc`, by the test beside this
 * file — the sweepstakes locale has to say "play" and "play amount" where the
 * English says "bet" and "stake", and a blank that falls through to English
 * would put a bet word on a sweepstakes screen.
 */
export function birdieAutoBetCopy(t: Translate) {
  return {
    title: t('Auto bet'),
    zoneLabel: t('Zone'),
    /**
     * The four named zones. There is no fifth word for a hand-set band: the
     * slider is always on show and setting it by hand simply lights none of
     * these, so the chip that used to say so was a control with nothing left
     * to do (owner 2026-09-17).
     */
    presets: {
      short: t('Short'),
      middle: t('Middle'),
      long: t('Long'),
      far: t('Far')
    },
    marketsLabel: t('Markets'),
    markets: {
      exact: t('Exact order'),
      count: t('Count'),
      both: t('Both')
    },
    /**
     * The markets TAB's own word for exact order (owner 2026-09-18: "maybe we
     * should just call it 'exact' instead of 'exact order'"). A NEW key rather
     * than a rename of `Exact order`: that one is the BOARD's column heading and
     * the rules panel's family name, it is borrowed from the forecast games and
     * already ships in sixteen locales, and the owner was talking about the tab.
     * So the two live side by side and the board is untouched. `en-soc` takes
     * the English — the word says neither bet, stake, odds nor win.
     */
    marketExactShort: t('Exact'),
    stakePerOption: t('Stake per option'),
    maxPerRound: t('Max per round'),
    /**
     * The ceiling is set as a COUNT of options, so the two ends of its ladder
     * are a number of them and no limit at all. The open end is drawn as an ∞
     * rather than printed — a sentence in a 60-px slot clips in a third of the
     * locales — so this is the reading's NAME (its `aria-label` and its
     * tooltip) and the one word the money line has for that rung.
     */
    noLimit: t('No limit'),
    nOptions: (count: number): string => t('{{count}} options', { count }),
    stopAfter: t('Stop after'),
    /** The rounds ladder's own open end, drawn and named the same way. */
    untilIStop: t('Until I stop'),
    /**
     * An armed rule counting down can be on one last round, and the stepper
     * shows whatever the draft holds — so the singular is a sentence of its
     * own. Two literals rather than an i18next plural suffix: the board's own
     * precedent, and the only shape the sync script's English-keyed map carries.
     */
    nRounds: (count: number): string =>
      count === 1 ? t('{{count}} round', { count }) : t('{{count}} rounds', { count }),
    /** The four stepper arrows: one glyph each, so the name has to say what it steps. */
    fewerOptions: t('Fewer options'),
    moreOptions: t('More options'),
    fewerRounds: t('Fewer rounds'),
    moreRounds: t('More rounds'),
    roundTypeTitle: t('Frost / Fire every round'),
    frost: t('Frost'),
    fire: t('Fire'),
    /**
     * The quiet line above the button, and the only money the sheet quotes:
     * what the ceiling's option count costs at the chosen stake. It replaced a
     * worked example of the open round — which named whichever tiles happened
     * to be priced at that moment, and said nothing at all between rounds
     * (owner 2026-09-17). `amount` is the ladder's no-limit word at the top of
     * the ladder, since a ceiling of no limit is not a sum of money.
     */
    /**
     * A round the ceiling trimmed. Said out loud, because a rule that quietly
     * placed four of nine would be the feature deciding on its own.
     */
    capped: (placed: number, caught: number, amount: string): string =>
      t('Placed {{placed}} of {{caught}} — your {{amount}} round max', { placed, caught, amount }),
    maxStakePerRound: (amount: string): string => t('Max stake per round {{amount}}', { amount }),
    /**
     * The board's own way in while the strip is gone — and the arming button's
     * own verb whenever the sheet has no round to place on. It says SET UP
     * rather than start: pressed on a running round the rule is armed and
     * places nothing until the next one opens, and a button that said start
     * would promise a bet this round.
     */
    setUp: t('Set up auto bet'),
    start: t('Start auto bet'),
    /** The armed sheet's primary: the running rule takes the draft's settings. */
    update: t('Update auto bet'),
    stop: t('Stop auto bet'),
    close: t('Close'),
    andUp: (min: string): string => t('{{min}} and up', { min }),
    between: (min: string, max: string): string => t('{{min}} – {{max}}', { min, max }),
    pillLeft: (count: number): string => t('{{count}} left', { count }),
    pillUntilStopped: t('until stopped'),
    /** Why a round was skipped — the pill's line for that round's window, and the notice. */
    skip: {
      unpriced: t('No prices this round — skipped'),
      nothingInZone: (zone: string): string =>
        t('Nothing in {{zone}} this round — skipped', { zone }),
      clamped: t('A stake is over this round’s max — skipped'),
      overCeiling: (amount: string): string =>
        t('Over your {{amount}} max per round — skipped', { amount }),
      balance: t('Not enough balance — auto bet stopped')
    },
    /** Why the rule stopped — one sentence per `AutoBetStopReason`. */
    stopped: {
      player: t('Auto bet off'),
      manualEdit: t('Auto bet off — you’re placing by hand'),
      balance: t('Not enough balance — auto bet stopped'),
      finished: t('Auto bet finished')
    },
    ariaOpen: t('Auto bet settings'),
    ariaMin: t('Zone minimum'),
    ariaMax: t('Zone maximum')
  }
}

export type BirdieAutoBetCopy = ReturnType<typeof birdieAutoBetCopy>

/**
 * A zone as a sentence: `zoneLabel` says which of the two shapes applies and
 * hands over the formatted bounds; this picks the sentence. The sheet's value
 * line, the pill and the empty-zone skip all read the same words.
 */
export function autoBetZoneText(
  copy: BirdieAutoBetCopy,
  zone: ZoneBounds,
  formatMultiplier: (m: number) => string
): string {
  const label = zoneLabel(zone, formatMultiplier)
  return label.kind === 'andUp' ? copy.andUp(label.min) : copy.between(label.min, label.max)
}

/**
 * Why a round was skipped, as the one sentence the pill and the board's notice
 * show for it. Two of the five name a number — the zone that caught nothing,
 * the ceiling that was crossed — and the caller formats both, since only it
 * knows the currency.
 */
export function autoBetSkipText(
  copy: BirdieAutoBetCopy,
  skip: AutoBetSkip,
  parts: { zone: string; ceiling: string }
): string {
  switch (skip) {
    case 'unpriced':
      return copy.skip.unpriced
    case 'nothing-in-zone':
      return copy.skip.nothingInZone(parts.zone)
    case 'clamped':
      return copy.skip.clamped
    case 'over-ceiling':
      return copy.skip.overCeiling(parts.ceiling)
    case 'balance':
      return copy.skip.balance
  }
}
